import { useEffect } from 'react'
import { useChatStore } from '../../../stores/chatStore'
import { ChatService } from '../../../services/ChatService'
import { v4 as uuidv4 } from 'uuid'

const RETRY_INTERVAL = 30000 // 30s
const MAX_RETRIES = 5

export const OutboxWorker = () => {
    const pendingMessages = useChatStore(state => state.pendingMessages)
    const removeFromOutbox = useChatStore(state => state.removeFromOutbox)
    const updateOutboxMessage = useChatStore(state => state.updateOutboxMessage)

    // Actions needed for optimistic updates re-sync
    // Actions that might be needed for sync updates
    const addMessage = useChatStore(state => state.addMessage)

    useEffect(() => {
        const processQueue = async () => {
            if (!navigator.onLine || pendingMessages.length === 0) return

            for (const msg of pendingMessages) {
                if (msg.status === 'sending') continue
                if (msg.retryCount >= MAX_RETRIES) {
                    console.error('[OutboxWorker] Max retries reached for msg', msg.id)
                    removeFromOutbox(msg.id)
                    // Optional: Notify user of permanent failure via Toast
                    continue
                }

                // Lock message
                updateOutboxMessage(msg.id, { status: 'sending', lastAttempt: Date.now() })
                console.log('[OutboxWorker] Retrying message:', msg.id)

                try {
                    // Re-run the send logic (Simplified for Background Worker)
                    // Note: This duplicates some logic from useChatMessages but decouples UI state

                    // 1. Ensure user message is persisted/exists
                    await ChatService.saveUserMessage(msg.conversationId, msg.content)

                    // 2. Stream Response
                    const assistantMessageId = uuidv4()
                    let fullContent = ''
                    const streamId = `stream-${Date.now()}`

                    // We don't have onMessage hooks here to update UI incrementally 
                    // unless we write to store 'streamingMessage' for that specific agent.
                    // Ideally we SHOULD update store so if user opens that chat, they see progress.

                    await ChatService.streamChat({
                        agentId: msg.agentId,
                        conversationId: msg.conversationId,
                        messages: [{ role: 'user', content: msg.content }], // Simplified history (Risk: context loss)
                        messageId: assistantMessageId,
                        retryAttempt: msg.retryCount + 1,
                        onOpen: () => {
                            useChatStore.getState().setStreamingMessage(msg.agentId, {
                                id: streamId,
                                role: 'assistant',
                                content: '',
                                isStreaming: true,
                                timestamp: new Date().toISOString()
                            })
                        },
                        onMessage: (delta) => {
                            fullContent += delta
                            useChatStore.getState().setStreamingMessage(msg.agentId, {
                                id: streamId,
                                role: 'assistant',
                                content: fullContent,
                                isStreaming: true,
                                timestamp: new Date().toISOString()
                            })
                        },
                        onClose: () => {
                            useChatStore.getState().setStreamingMessage(msg.agentId, null)

                            if (fullContent.trim()) {
                                addMessage(msg.agentId, {
                                    id: assistantMessageId,
                                    conversation_id: msg.conversationId,
                                    role: 'assistant',
                                    content: fullContent,
                                    created_at: new Date().toISOString(),
                                    isHtml: false
                                })
                            }
                            // Success!
                            removeFromOutbox(msg.id)
                        },
                        onError: (err) => {
                            throw err
                        }
                    })

                } catch (err) {
                    console.error('[OutboxWorker] Retry failed:', err)
                    updateOutboxMessage(msg.id, {
                        status: 'failed',
                        retryCount: msg.retryCount + 1
                    })
                }
            }
        }

        const interval = setInterval(processQueue, RETRY_INTERVAL)
        window.addEventListener('online', processQueue)

        return () => {
            clearInterval(interval)
            window.removeEventListener('online', processQueue)
        }
    }, [pendingMessages, removeFromOutbox, updateOutboxMessage, addMessage])

    return null
}
